import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle, PhoneCall, Plug, Workflow } from 'lucide-react';
import { SlideStage } from '../../components/deck/SlideStage';
import { VexuCallsShowcase } from '../../components/deck/VexuCallsShowcase';
import { businessChannels, goLiveSteps, sharedCapabilities, typicalDeployment } from '../../data/deckSlide8';
const CHANNEL_ICONS = {
  voice: PhoneCall,
  whatsapp: MessageCircle,
  integrations: Plug,
  workflows: Workflow
} as const;
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const PORTAL_IMAGE = 'https://cdn.magicpatterns.com/uploads/jejmyMHibCtAaer6jkDBG4/ChatGPT_Image_Jul_4,_2026,_04_54_57_PM.png';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4;
export function PlatformSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => {
      timers.push(window.setTimeout(() => setStage(next), ms));
    };
    at(750, 1); // shared engine
    at(2200, 2); // business portal
    at(4200, 3); // configure → forward → live
    at(6200, 4); // deployment comparison
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2505" data-mp="15skmllicmey7">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2506" data-mp="15rszkz2thpjj">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 65% at 6% -18%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 60%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2507" data-mp="1ar5odpd916or" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2508" data-mp="2r5e0eq0e7xm5">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2509" data-mp="2trels212ky21" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2510" data-mp="2pnp3fuazncpg">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2511" data-mp="2kr8dcuk8ao9q">
          08 · VEXU Business
        </motion.span>

        {/* Headline */}
        <h1 className="absolute text-[42px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 132,
        width: 760
      }} data-id="element-2512" data-mp="33moq4wexxhiz">
          {['One AI platform.', 'From individuals to entire businesses.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: 0.15 + i * 0.15,
          ease
        }} data-id="element-2513" data-mp="3rd2bk430si4l" data-mpi={i}>
                {line}
              </motion.span>)}
        </h1>
        <motion.p className="absolute text-[17px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 250,
        width: 520
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.7,
        ease
      }} data-id="element-2514" data-mp="hhei85mqe9tv">
          The same telephony, voice, memory and action layer powers VEXU
          Business.
        </motion.p>

        {/* LEFT — the consumer product, now smaller */}
        <motion.div className="absolute" style={{
        left: 112,
        top: 326
      }} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.5,
        ease
      }} data-id="element-2515" data-mp="106l2hx36b9hs">
          <VexuCallsShowcase scale={0.38} data-id="element-2516" data-mp="26we41guhgbfm" />
        </motion.div>
        <motion.div className="absolute" style={{
        left: 112,
        top: 674,
        width: 200
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        delay: 0.9,
        ease
      }} data-id="element-2517" data-mp="26ut8pp0cgogp">
          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#0B1712]" data-id="element-2518" data-mp="2bndzsirv8ngj">
            VEXU App
          </p>
          <p className="mt-1.5 text-[13px] text-[#7C8B82]" data-id="element-2519" data-mp="1nylc19ledlx0">
            Individuals &amp; professionals
          </p>
        </motion.div>

        {/* CENTER — the shared engine */}
        <div className="absolute" style={{
        left: 300,
        top: 326,
        width: 300
      }} data-id="element-2520" data-mp="1wzswanr2wm83">
          {/* connectors */}
          <motion.div className="absolute h-px" style={{
          left: 8,
          top: 168,
          backgroundColor: 'rgba(0,200,83,0.4)'
        }} initial={{
          width: 0
        }} animate={{
          width: shown(1) ? 52 : 0
        }} transition={{
          duration: 0.7,
          ease
        }} data-id="element-2521" data-mp="1ryvkcswnwof1" />
          <motion.div className="absolute h-px" style={{
          left: 232,
          top: 168,
          backgroundColor: 'rgba(0,200,83,0.4)'
        }} initial={{
          width: 0
        }} animate={{
          width: shown(2) ? 96 : 0
        }} transition={{
          duration: 0.7,
          ease
        }} data-id="element-2522" data-mp="3burgt32i79ax" />

          <motion.div className="absolute text-center" style={{
          left: 58,
          top: 96,
          width: 180
        }} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: shown(1) ? 1 : 0,
          y: shown(1) ? 0 : 10
        }} transition={{
          duration: 0.8,
          ease
        }} data-id="element-2523" data-mp="36ykle47qhg78">
            <div className="mx-auto grid h-11 w-11 place-items-center rounded-full" style={{
            backgroundColor: 'rgba(0,200,83,0.10)'
          }} data-id="element-2524" data-mp="3kuoaxk7nx0dd">
              <img src={VEXU_LOGO} alt="" className="h-6 w-6 object-contain" data-id="element-2525" data-mp="heh7net5t554" />
            </div>
            <p className="mt-3 text-[14px] font-semibold tracking-[-0.01em] text-[#0B1712]" data-id="element-2526" data-mp="m7tk6qkkdlhi">
              VEXU AI
            </p>
            <div className="mt-2.5 space-y-1" data-id="element-2527" data-mp="r3eeuliutu6s">
              {[`${sharedCapabilities[0].label} · ${sharedCapabilities[1].label}`, `${sharedCapabilities[2].label} · ${sharedCapabilities[3].label}`].map((line, i) => <motion.p key={line} className="text-[11px] uppercase tracking-[0.16em] text-vexu-700" initial={{
              opacity: 0
            }} animate={{
              opacity: shown(1) ? 1 : 0
            }} transition={{
              duration: 0.5,
              delay: 0.35 + i * 0.18,
              ease
            }} data-id="element-2528" data-mp="i2g8vxouvclk" data-mpi={i}>
                  {line}
                </motion.p>)}
            </div>
          </motion.div>
        </div>

        {/* RIGHT — the business reveal */}
        <motion.div className="absolute" style={{
        left: 668,
        top: 232
      }} initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: shown(2) ? 1 : 0,
        y: shown(2) ? 0 : 18
      }} transition={{
        duration: 1,
        ease
      }} data-id="element-2529" data-mp="3qhq5iq9z7ply">
          <img src={PORTAL_IMAGE} alt="Vexu Business portal — call flow builder" style={{
          width: 654,
          height: 436
        }} className="object-contain" data-id="element-2530" data-mp="25zpd4nhcoar0" />
        </motion.div>
        <motion.div className="absolute" style={{
        left: 706,
        top: 636,
        width: 340
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(2) ? 1 : 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease
      }} data-id="element-2531" data-mp="2f0naimxa9k0m">
          <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-[#0B1712]" data-id="element-2532" data-mp="1rb2ofv5u26n4">
            VEXU Business
          </p>
          <p className="mt-1.5 text-[13px] text-[#7C8B82]" data-id="element-2533" data-mp="1w3vuqngy9n1b">
            AI call handling for businesses
          </p>
        </motion.div>

        {/* Channel rail — the same engine, expanded for business */}
        <div className="absolute" style={{
        left: 1344,
        top: 224,
        width: 144
      }} data-id="element-2534" data-mp="13k72heo265ns">
          <motion.p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-vexu-700" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(2) ? 1 : 0
        }} transition={{
          duration: 0.7,
          delay: 0.3,
          ease
        }} data-id="element-2535" data-mp="3t4v4c5he1l40">
            One AI platform
          </motion.p>

          {/* the spine that ties every channel back to the engine */}
          <motion.div className="absolute w-px" style={{
          left: -14,
          top: 44,
          backgroundColor: 'rgba(0,200,83,0.3)'
        }} initial={{
          height: 0
        }} animate={{
          height: shown(2) ? 302 : 0
        }} transition={{
          duration: 0.9,
          delay: 0.45,
          ease
        }} data-id="element-2536" data-mp="fwie54rhpynf" />

          {businessChannels.map((channel, i) => {
          const Icon = CHANNEL_ICONS[channel.id];
          return <motion.div key={channel.id} className="absolute" style={{
            left: 0,
            top: 52 + i * 78,
            width: 148
          }} initial={{
            opacity: 0,
            x: -6
          }} animate={{
            opacity: shown(2) ? 1 : 0,
            x: shown(2) ? 0 : -6
          }} transition={{
            duration: 0.7,
            delay: 0.6 + i * 0.16,
            ease
          }} data-id="element-2537" data-mp="ylyoskww37hk" data-mpi={i}>
                <span className="absolute h-px" style={{
              left: -14,
              top: 9,
              width: 10,
              backgroundColor: 'rgba(0,200,83,0.45)'
            }} data-id="element-2538" data-mp="18bdpighv3wng" />
                <span className="flex items-center gap-1.5" data-id="element-2539" data-mp="zcs7cmgayjn7">
                  <Icon className="h-[13px] w-[13px] text-vexu-700" data-id="element-2540" data-mp="knnqb8l8ulgo" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0B1712]" data-id="element-2541" data-mp="pke75y4vhl35">
                    {channel.label}
                  </span>
                </span>
                <p className="mt-1 text-[11.5px] leading-snug text-[#7C8B82]" data-id="element-2542" data-mp="gkzh2zsmbcj6">
                  {channel.caption}
                </p>
                {channel.detail && <p className="mt-0.5 text-[10.5px] leading-snug text-[#A3AFA7]" data-id="element-2543" data-mp="3ecmmqlw4xwhr">
                    {channel.detail}
                  </p>}
              </motion.div>;
        })}
        </div>

        {/* Configure → Forward → Live */}
        <div className="absolute flex items-center gap-6" style={{
        left: 1074,
        top: 630
      }} data-id="element-2544" data-mp="3a9873wc3zi4u">
          {goLiveSteps.map((step, i) => <React.Fragment key={step.label} data-id="element-2545" data-mp="5uq80zqhzlkw" data-mpi={i}>
              {i > 0 && <motion.span initial={{
            opacity: 0
          }} animate={{
            opacity: shown(3) ? 1 : 0
          }} transition={{
            duration: 0.4,
            delay: 0.2 + i * 0.45,
            ease
          }} data-id="element-2546" data-mp="3o8hfwaxzj9ys">
                  <ArrowRight className="h-3.5 w-3.5 text-vexu-700/50" data-id="element-2547" data-mp="krh8lcbwr3c3" />
                </motion.span>}
              <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: shown(3) ? 1 : 0,
            y: shown(3) ? 0 : 6
          }} transition={{
            duration: 0.55,
            delay: i * 0.45,
            ease
          }} data-id="element-2548" data-mp="pj929vqd1p6g">
                <p className="text-[10.5px] font-semibold tracking-[0.2em] text-vexu-700/60" data-id="element-2549" data-mp="18m16ekh8hrzj">
                  {step.index}
                </p>
                <p className="mt-1 text-[15.5px] font-medium text-[#0B1712]" data-id="element-2550" data-mp="13qc3c7609syh">
                  {step.label}
                </p>
              </motion.div>
            </React.Fragment>)}
        </div>
        <motion.p className="absolute text-[13px] text-[#7C8B82]" style={{
        left: 1074,
        top: 686,
        width: 400
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(3) ? 1 : 0
      }} transition={{
        duration: 0.7,
        delay: 1.35,
        ease
      }} data-id="element-2551" data-mp="pwl3jqi2tnfy">
          Forward activates the voice channel. Keep your existing business
          number.
        </motion.p>

        {/* Deployment comparison */}
        <motion.div className="absolute flex items-start gap-14 border-t border-[#0B1712]/[0.09] pt-7" style={{
        left: 112,
        right: 112,
        bottom: 92
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2552" data-mp="24e4jp27j2jhg">
          <div data-id="element-2553" data-mp="2ibt305ly5ike">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-[#A3AFA7]" data-id="element-2554" data-mp="2icclbnfie8yy">
              Typical AI voice deployment
            </p>
            <span className="mt-3 flex items-center gap-2.5" data-id="element-2555" data-mp="1pnx54xkn5km1">
              {typicalDeployment.map((item, i) => <React.Fragment key={item} data-id="element-2556" data-mp="1umi7b2jgeed4" data-mpi={i}>
                  {i > 0 && <span className="text-[11px] text-[#C3CCC5]" data-id="element-2557" data-mp="1zerwuhgnpcev">→</span>}
                  <span className="text-[13px] text-[#A3AFA7]" data-id="element-2558" data-mp="11z5jkp1k40i8">{item}</span>
                </React.Fragment>)}
            </span>
          </div>

          <span className="mt-1 h-10 w-px bg-[#0B1712]/10" data-id="element-2559" data-mp="16w4h7hkfipl6" />

          <div data-id="element-2560" data-mp="zepourk2pyux">
            <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-vexu-700" data-id="element-2561" data-mp="zd4zdgcm963x">
              VEXU
            </p>
            <p className="mt-3 text-[19px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2562" data-mp="udij0y1hr9qw">
              One platform. One onboarding experience.
            </p>
          </div>
        </motion.div>

        <motion.p className="absolute text-[15px] font-medium text-[#33463B]" style={{
        left: 112,
        bottom: 44
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        delay: 0.3,
        ease
      }} data-id="element-2563" data-mp="kjjj85ow4224">
          Turn AI voice deployment into a software onboarding experience.
        </motion.p>
      </div>
    </SlideStage>;
}